// `visibilitychange` state reconciliation (docs/06-clients.md §4): iOS
// suspends PWA timers in the background, so on returning to the foreground a
// tab must not trust its in-memory picture of the world.
//
//  - Leader tab becomes visible  → re-sync with the server (the injected
//    callback typically kicks the signaling client / refetches screen data).
//  - Follower tab becomes visible → ask the leader for its current state over
//    the tabs channel so the "active in another tab" UI is fresh.

import type { TabElection } from './election.js';

/** Structural subset of `document` used for visibility tracking. */
export interface VisibilityDocLike {
  readonly visibilityState: 'visible' | 'hidden';
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface VisibilityReconcilerDeps {
  doc: VisibilityDocLike;
  election: TabElection;
  /** Leader-side re-sync: reconcile engine/screen state with the server. */
  onLeaderVisible: () => void;
}

/** Install the reconciler; returns a teardown function. */
export function installVisibilityReconciler(deps: VisibilityReconcilerDeps): () => void {
  const onChange = (): void => {
    if (deps.doc.visibilityState !== 'visible') return;
    if (deps.election.state.get().role === 'leader') {
      deps.onLeaderVisible();
    } else {
      deps.election.requestSync();
    }
  };
  deps.doc.addEventListener('visibilitychange', onChange);
  return () => deps.doc.removeEventListener('visibilitychange', onChange);
}
